import api from '@/services/api'
import type { MarketInfo } from './types'
import type { Tick } from '@/utils/ticks'
import type { MarketType, OhlcvTuple } from '@/utils/types'
import type { LimitSide, SymbolLimits } from './types'

export const EyeV2Service = {
  fetchMarketsInfo(
    marketType: MarketType,
    asset: string,
    exs: string,
    sellAsset: string | null,
    quote?: string,
  ) {
    const params = new URLSearchParams()
    if (sellAsset) params.set('sellAsset', sellAsset)
    if (quote) params.set('quote', quote)
    const query = params.toString() ? `?${params.toString()}` : ''
    return api.get<MarketInfo[]>(`/v2/eye/${marketType}/${asset}/${exs}${query}`)
  },
  fetchTime() {
    return api.get<number>('/v2/time')
  },
  fetchOhlcv(
    exchange: string,
    marketType: MarketType,
    asset: string,
    tf: string = '15m',
    quote?: string,
  ) {
    const quoteParam = quote ? `&quote=${quote}` : ''
    return api.get<OhlcvTuple[]>(
      `/v2/eye/${exchange}/${marketType}/${asset}/ohlcv?tf=${tf}${quoteParam}`,
    )
  },
  checkExistence(
    exBuy: string,
    exSell: string,
    asset: string,
    marketType: MarketType,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) {
    const params = new URLSearchParams()
    if (sellAsset) params.set('sellAsset', sellAsset)
    if (quoteBuy) params.set('quote_buy', quoteBuy)
    if (quoteSell) params.set('quote_sell', quoteSell)
    const query = params.toString() ? `?${params.toString()}` : ''
    return api.get(`/v2/eye/${exBuy}/${exSell}/${marketType}/${asset}/check${query}`)
  },
  fetchLimits(
    exBuy: string,
    exSell: string,
    asset: string,
    marketType: string,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) {
    const params = new URLSearchParams()
    if (sellAsset) params.set('sellAsset', sellAsset)
    if (quoteBuy) params.set('quote_buy', quoteBuy)
    if (quoteSell) params.set('quote_sell', quoteSell)
    const query = params.toString() ? `?${params.toString()}` : ''
    return api.get<Record<LimitSide, SymbolLimits>>(
      `/v2/eye/${exBuy}/${exSell}/${marketType}/${asset}/limits${query}`,
    )
  },
  fetchOhlcv1m(
    exBuy: string,
    exSell: string,
    asset: string,
    marketType: string,
    sellAsset?: string | null,
    quoteBuy?: string,
    quoteSell?: string,
  ) {
    const params = new URLSearchParams()
    if (sellAsset) params.set('sellAsset', sellAsset)
    if (quoteBuy) params.set('quote_buy', quoteBuy)
    if (quoteSell) params.set('quote_sell', quoteSell)
    const query = params.toString() ? `?${params.toString()}` : ''
    return api.get<Array<OhlcvTuple[]>>(
      `/v2/eye/${exBuy}/${exSell}/${marketType}/${asset}/ohlcv/1m${query}`,
    )
  },
  fetchPool(poolAddress: string, exchange: string) {
    return api.get(`/v2/eye/pool/${poolAddress}/${exchange}`)
  },
  fetchMarket(exchange: string, symbol: string) {
    return api.get(`/v2/eye/market/${exchange}/${symbol}`)
  },
  fetchSymbolMarketTicks(exchange: string, symbol: string) {
    return api.get<Tick[]>(`/v2/eye/${exchange}/${symbol}/market-ticks`)
  },
}
